import { ProgressCircle } from '@react-spectrum/s2';
import { memo, useCallback, useMemo, useRef, type KeyboardEvent } from 'react';

import { BlobImage } from '@/components/blob/BlobImage';
import { cn } from '@/utils/cn';

import type { EmailContent } from '../../../../bridge/types/artifact.ts';

import type { ArtifactComponentProps } from './types';
import { useEmailTemplates } from './useEmailTemplates';

const PLACEHOLDER_THUMBNAIL =
	'https://placehold.co/400x300/e2e8f0/94a3b8?text=No+Preview';

type EmailTemplateSelectionProps = ArtifactComponentProps<EmailContent> & {
	pendingTemplateId: string | null;
	onPendingChange: (templateId: string) => void;
};

export const EmailTemplateSelection = memo(function EmailTemplateSelection({
	pendingTemplateId,
	onPendingChange,
	isReadOnly = false,
}: EmailTemplateSelectionProps) {
	const { templates, isLoading, error } = useEmailTemplates();
	const tileRefs = useRef<Array<HTMLButtonElement | null>>([]);

	const focusedIndex = useMemo(() => {
		if (!pendingTemplateId) return 0;
		const idx = templates.findIndex((t) => t.id === pendingTemplateId);
		return idx >= 0 ? idx : 0;
	}, [pendingTemplateId, templates]);

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLDivElement>) => {
			if (isReadOnly || templates.length === 0) return;
			const { key } = event;
			if (
				key !== 'ArrowRight' &&
				key !== 'ArrowLeft' &&
				key !== 'ArrowUp' &&
				key !== 'ArrowDown'
			) {
				return;
			}
			event.preventDefault();
			const delta = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;
			const nextIdx =
				(focusedIndex + delta + templates.length) % templates.length;
			const nextTemplate = templates[nextIdx];
			if (!nextTemplate) return;
			onPendingChange(nextTemplate.id);
			tileRefs.current[nextIdx]?.focus();
		},
		[focusedIndex, isReadOnly, onPendingChange, templates],
	);

	if (isLoading) {
		return (
			<div className="flex items-center gap-3 py-4">
				<ProgressCircle isIndeterminate size="S" aria-label="Loading" />
				<span className="text-sm text-(--text-muted)">
					Loading templates...
				</span>
			</div>
		);
	}

	if (error) {
		return <p className="text-sm text-red-600">{error}</p>;
	}

	if (templates.length === 0) {
		return (
			<p className="text-sm text-(--text-muted)">
				No email templates available.
			</p>
		);
	}

	return (
		<div
			role="radiogroup"
			aria-label="Email templates"
			className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-6 p-1"
			onKeyDown={handleKeyDown}
		>
			{templates.map((template, idx) => {
				const isSelected = template.id === pendingTemplateId;
				const label = template.name ?? template.id;
				return (
					<button
						key={template.id}
						ref={(el) => {
							tileRefs.current[idx] = el;
						}}
						type="button"
						role="radio"
						aria-checked={isSelected}
						aria-label={label}
						data-testid={`card-${template.id}`}
						tabIndex={isReadOnly ? -1 : idx === focusedIndex ? 0 : -1}
						disabled={isReadOnly}
						onClick={() => {
							if (isReadOnly) return;
							onPendingChange(template.id);
						}}
						className={cn(
							`group flex flex-col overflow-hidden rounded-[10px] bg-white
							text-left transition-shadow`,
							'shadow-[0_0_0_2px_var(--card-frame)] [--card-frame:#e9e9e9]',
							'focus:outline-none focus-visible:[--card-frame:#3b63fb]',
							isSelected && '[--card-frame:#000000]',
							isReadOnly && 'cursor-not-allowed opacity-60',
						)}
					>
						<div
							className="relative aspect-[4/3] w-full overflow-hidden
								bg-gray-50"
						>
							<BlobImage
								src={template.thumbnail_link ?? PLACEHOLDER_THUMBNAIL}
								alt={label}
								className="h-full w-full object-cover object-top"
							/>
						</div>
						<div className="px-4 py-3">
							<p className="truncate text-sm font-medium text-gray-900">
								{label}
							</p>
						</div>
					</button>
				);
			})}
		</div>
	);
});
